import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TrapFocus from '@mui/material/Unstable_TrapFocus';
import clsx from 'clsx';
import { useCallback, useMemo, useRef } from 'react';

import type { PopperPlacementType, PopperProps } from '@mui/material/Popper';

/** CSS custom property the height-cap modifier writes; the Paper reads it for `maxHeight`. */
const MAX_HEIGHT_CSS_VAR = '--smart-popover-max-height';

type PopperModifier = NonNullable<PopperProps['modifiers']>[number];
type PopperModifierArgs = Parameters<NonNullable<PopperModifier['fn']>>[0];
type PopperInstance = NonNullable<PopperProps['popperRef']> extends React.Ref<infer T> ? T : never;

/**
 * The reason a {@link SmartPopover} requested to close, mirroring the subset of
 * Material UI's `<Popover>` close reasons that this component can produce.
 */
export type SmartPopoverCloseReason = 'clickAway' | 'escapeKeyDown';

export interface SmartPopoverProps {
	/** Whether the popover is shown. */
	open: boolean;
	/** The element the popover is anchored to; the popover opens from its bottom-left. */
	anchorEl: HTMLElement | null;
	/**
	 * Called when the popover should close — on an outside click or the Escape key.
	 * Clicks on `anchorEl` itself are ignored so the trigger's own handler can toggle.
	 */
	onClose: (event: MouseEvent | TouchEvent | React.KeyboardEvent, reason: SmartPopoverCloseReason) => void;
	children: React.ReactNode;
	/** Guaranteed clearance (px) between the popover and the viewport edge it opens toward. @default 24 */
	gap?: number;
	/** Space (px) between the popover and its anchor, on whichever side it opens. @default 0 */
	offset?: number;
	/** Minimum height (px) the popover keeps even when the anchor sits too close to the edge. @default 120 */
	minHeight?: number;
	/**
	 * Minimum room (px) that must exist between the anchor and a viewport edge for the popover to
	 * open toward that edge. The popover prefers opening downward and only opens upward when there
	 * is less than this much room below the anchor. The choice depends solely on the anchor's
	 * position — never on the popover's content size. @default 160
	 */
	directionMargin?: number;
	/** Render into the parent's DOM hierarchy instead of a portal. @default false */
	disablePortal?: boolean;
	/** Enter/exit transition duration (ms); pass 0 to open and close instantly. @default auto */
	transitionDuration?: number;
	/** Applied to the Popper root (e.g. for `zIndex`). */
	style?: React.CSSProperties;
	/** Applied to the height-capped Paper surface (which clips overflow). */
	paperClassName?: string;
}

/**
 * Maps a resolved Popper placement to the CSS `transform-origin` the Grow transition
 * should animate from, so a downward popover grows from its top edge and an upward
 * (flipped) popover grows from its bottom edge.
 *
 * @param placement - The placement Popper resolved to after collision handling.
 * @returns A `transform-origin` value for the Grow wrapper.
 */
function transformOriginForPlacement(placement: PopperPlacementType | undefined): string {
	return placement?.startsWith('top') === true ? 'left bottom' : 'left top';
}

/**
 * Chooses whether the popover opens downward or upward based solely on how much room the anchor
 * has toward each viewport edge — never on the popover's content size. Opening downward is
 * preferred and is chosen whenever there is at least `margin` px below the anchor; otherwise the
 * popover opens upward if there is at least `margin` px above, falling back to the roomier side
 * when neither direction clears the margin.
 *
 * @param roomBelow - Pixels between the anchor's bottom and the viewport's bottom edge.
 * @param roomAbove - Pixels between the anchor's top and the viewport's top edge.
 * @param margin - Minimum room required to open toward an edge.
 * @returns The start placement to use: `'bottom-start'` (downward) or `'top-start'` (upward).
 */
function chooseStartPlacement(roomBelow: number, roomAbove: number, margin: number): 'bottom-start' | 'top-start' {
	if (roomBelow >= margin) {
		return 'bottom-start';
	}
	if (roomAbove >= margin) {
		return 'top-start';
	}
	return roomBelow >= roomAbove ? 'bottom-start' : 'top-start';
}

/**
 * A `<Popover>` replacement built on `<Popper>` that prefers opening downward from its anchor,
 * opens upward when the anchor sits too close to the bottom of the viewport, caps its height to
 * the space available in whichever direction it opens and clips overflow (leaving any scrolling to
 * its content), and re-adds the UX `<Popper>` lacks: click-away dismissal, Escape-to-close, focus
 * management, an enter/exit transition, and Paper elevation.
 *
 * The open direction is chosen from the anchor's distance to each viewport edge versus
 * `directionMargin` — never from the popover's content size — and is re-evaluated only when the
 * anchor moves (e.g. the container scrolls), so growing or shrinking the content never flips the
 * direction. Height capping tracks the chosen direction: a custom Popper modifier computes the
 * available height on every reposition and writes it to a CSS variable the Paper consumes.
 *
 * @param props - Component props.
 * @param props.open - Whether the popover is shown.
 * @param props.anchorEl - The element the popover is anchored to.
 * @param props.onClose - Called on an outside click or the Escape key.
 * @param props.children - Content rendered inside the height-capped Paper (which clips overflow, so the content manages its own scrolling).
 * @param props.gap - Guaranteed clearance (px) from the viewport edge.
 * @param props.offset - Space (px) between the popover and its anchor.
 * @param props.minHeight - Minimum height (px) kept when the anchor sits near the edge.
 * @param props.directionMargin - Room (px) required toward an edge to open in that direction.
 * @param props.disablePortal - Render into the parent's DOM hierarchy instead of a portal.
 * @param props.transitionDuration - Enter/exit transition duration (ms); 0 opens and closes instantly.
 * @param props.style - Applied to the Popper root (e.g. for `zIndex`).
 * @param props.paperClassName - Applied to the scrolling Paper surface.
 * @returns The popover element, or nothing rendered while closed.
 */
export function SmartPopover({
	open,
	anchorEl,
	onClose,
	children,
	gap = 24,
	offset = 0,
	minHeight = 120,
	directionMargin = 160,
	disablePortal = false,
	transitionDuration,
	style,
	paperClassName,
}: SmartPopoverProps) {
	const popperRef = useRef<PopperInstance | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);

	// Popper.js only repositions on ancestor scroll / window resize, not on changes to the
	// floating element's own size. Without this, an upward-opening popover keeps its top pinned
	// and grows downward (or scrolls at a stale cap) as content resizes. Observing the Paper and
	// forcing an update re-derives `top = anchorTop - height`, so its bottom stays anchored and it
	// grows upward instead. The direction itself is content-independent, so this never flips it.
	const setPaperRef = useCallback((node: HTMLElement | null) => {
		resizeObserverRef.current?.disconnect();
		if (node === null) {
			resizeObserverRef.current = null;
			return;
		}
		const observer = new ResizeObserver(() => {
			void popperRef.current?.update();
		});
		observer.observe(node);
		resizeObserverRef.current = observer;
	}, []);

	const modifiers = useMemo<PopperProps['modifiers']>(
		() => [
			// Disable Popper's built-in content-fitting flip; `smartDirection` below replaces it.
			{ name: 'flip', enabled: false },
			{
				// Replaces Popper's built-in `flip`, which chooses direction by whether the content
				// *fits*. Here the direction depends only on the anchor's room to each viewport edge,
				// so resizing the content never changes it. `data._skip` is reset to `false` at the
				// start of every update cycle, so the decision re-runs on each reposition (scroll /
				// resize) without looping within a cycle.
				name: 'smartDirection',
				enabled: true,
				phase: 'main',
				data: { _skip: false },
				fn: ({ state, name }: PopperModifierArgs) => {
					const data = state.modifiersData[name] as { _skip?: boolean };
					if (data._skip === true) {
						return;
					}
					const referenceRect = state.elements.reference.getBoundingClientRect();
					const viewportHeight = state.elements.popper.ownerDocument.documentElement.clientHeight;
					const placement = chooseStartPlacement(
						viewportHeight - referenceRect.bottom,
						referenceRect.top,
						directionMargin,
					);
					if (placement !== state.placement) {
						data._skip = true;
						state.placement = placement;
						state.reset = true;
					}
				},
			},
			{ name: 'offset', options: { offset: [0, offset] } },
			{ name: 'preventOverflow', enabled: true, options: { mainAxis: true, altAxis: true, padding: gap } },
			{
				name: 'smartPopoverMaxHeight',
				enabled: true,
				// Runs after `smartDirection`/`preventOverflow` (main phase) so `state.placement` is final.
				phase: 'beforeWrite',
				requiresIfExists: ['offset', 'preventOverflow'],
				fn: ({ state }: PopperModifierArgs) => {
					const popperEl = state.elements.popper;
					if (!(popperEl instanceof HTMLElement)) {
						return;
					}
					const referenceRect = state.elements.reference.getBoundingClientRect();
					const viewportHeight = popperEl.ownerDocument.documentElement.clientHeight;
					const openingUpward = state.placement.startsWith('top');
					const available =
						openingUpward ? referenceRect.top - offset - gap : viewportHeight - referenceRect.bottom - offset - gap;
					popperEl.style.setProperty(MAX_HEIGHT_CSS_VAR, `${Math.max(minHeight, available)}px`);
				},
			},
		],
		[gap, offset, minHeight, directionMargin],
	);

	const handleClickAway = (event: MouseEvent | TouchEvent) => {
		// Ignore clicks on the trigger so it can toggle the popover via its own handler
		// rather than this listener closing it and the trigger immediately reopening it.
		if (anchorEl !== null && event.target instanceof Node && anchorEl.contains(event.target)) {
			return;
		}
		onClose(event, 'clickAway');
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Escape') {
			event.stopPropagation();
			onClose(event, 'escapeKeyDown');
		}
	};

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			popperRef={popperRef}
			placement='bottom-start'
			transition
			disablePortal={disablePortal}
			modifiers={modifiers}
			style={style}
		>
			{({ TransitionProps, placement }) => (
				<ClickAwayListener onClickAway={handleClickAway}>
					<Grow
						{...TransitionProps}
						timeout={transitionDuration}
						style={{ transformOrigin: transformOriginForPlacement(placement) }}
					>
						<Paper
							ref={setPaperRef}
							elevation={8}
							onKeyDown={handleKeyDown}
							className={clsx('flex flex-col overflow-hidden', paperClassName)}
							sx={{ maxHeight: `var(${MAX_HEIGHT_CSS_VAR}, none)` }}
						>
							<TrapFocus open={open} disableEnforceFocus>
								<div tabIndex={-1} className='flex min-h-0 flex-col'>
									{children}
								</div>
							</TrapFocus>
						</Paper>
					</Grow>
				</ClickAwayListener>
			)}
		</Popper>
	);
}
